import { useMemo } from "react";
import { P } from "../../../../../AbstractElements";
import {
  Action,
  admin,
  Amount,
  Attachement,
  danger,
  Details,
  hr,
  infoColor,
  light,
  manager,
  NA,
  pending,
  rejected,
  secondary,
  Status,
  success,
  Username,
  View,
  ViewMoreDetails,
  warningColor,
} from "../../../../../utils/Constant";
import BadgeDetails from "../../../../../CommonElements/Badge/BadgeDetails";
import { dateConversion } from "../../../../../utils/dateUtils";
import IconComponent from "../../../../../CommonElements/IconComponent";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";
import useUser from "../../../../../hooks/useUser";
import CustomDataTable from "../../../../../CommonElements/Table/CustomTable";
import {
  ConveyanceDetail,
  ConveyanceTableProps,
  IConveyanceServiceResponse,
} from "../../../types";
import { TableColumn } from "react-data-table-component";
import SkeletonTable from "../../../../../CommonElements/Loader/SkeletonTableLoader";

const ConveyanceTable = ({
  total,
  perPage,
  rowPerPageChange,
  pageChange,
  openModal,
  openDialog,
  conveyanceList,
  conveyanceListLoading,
  openAttachmentModal,
}: ConveyanceTableProps) => {
  const user = useUser();
  const { darkMode } = useAppSelector(({ layout }) => layout);

  const tableData = useMemo<
    (IConveyanceServiceResponse & { _rowClass: string })[]
  >(() => {
    const rows = Array.isArray(conveyanceList)
      ? conveyanceList
      : conveyanceList?.data.conveyance.data || [];

    return rows.map((row, i) => ({
      ...row,
      _rowClass: `row-animate row-delay-${i}`,
    }));
  }, [conveyanceList]);

  const columns = useMemo<TableColumn<any>[]>(() => {
    const baseCols: TableColumn<any>[] = [
      {
        name: "#",
        cell: (_: any, i: number) => (
          <div className="d-flex">
            <P className="f-12 m-0">
              {conveyanceList &&
                conveyanceList?.data &&
                conveyanceList?.data?.conveyance &&
                conveyanceList?.data?.conveyance?.from &&
                conveyanceList?.data?.conveyance?.from + i}
            </P>
          </div>
        ),
        sortable: true,
        width: "60px",
      },
      ...([admin, hr, manager].includes(user?.user_type as string)
        ? [
            {
              name: Username,
              cell: (row: ConveyanceDetail) => (
                <P className="m-0 f-12">{row?.username}</P>
              ),
              sortable: true,
              width: "120px",
            },
          ]
        : []),
      {
        name: "Date",
        cell: (row: ConveyanceDetail) => (
          <BadgeDetails
            color={light}
            className={`badget-light rounded-pill f-12 ${
              darkMode ? "text-white" : "text-dark"
            }`}
            text={dateConversion(row.date ?? "", true)}
          />
        ),
        sortable: true,
        width: "150px",
      },
      {
        name: Amount,
        cell: (row: ConveyanceDetail) => (
          <P className="m-0 f-12">{row.amount}</P>
        ),
        sortable: true,
        width: "100px",
      },
      {
        name: Attachement,
        cell: (row: ConveyanceDetail) => (
          <>
            {row.attachment ? (
              <div
                className="pointer"
                onClick={() => (row ? openAttachmentModal(row) : null)}
              >
                <BadgeDetails
                  className="f-12 rounded-pill"
                  color={infoColor}
                  text={View}
                />
              </div>
            ) : (
              <P className="m-0 f-12">{NA}</P>
            )}
          </>
        ),
        sortable: true,
        width: "130px",
      },
      {
        name: Status,
        cell: (row: ConveyanceDetail) => (
          <BadgeDetails
            className="text-capitalize f-10 rounded-pill w-100 text-wrap"
            color={
              (row.status.toLowerCase() === pending && warningColor) ||
              (row.status.toLowerCase() === rejected && danger) ||
              success
            }
            text={row.status ?? ""}
          />
        ),
        sortable: true,
        width: "150px",
      },
      {
        name: Details,
        cell: (row: ConveyanceDetail) => (
          <div className="pointer" onClick={() => openModal(row as any)}>
            <BadgeDetails
              color={secondary}
              className="badget-primary f-10 rounded-pill w-100 text-wrap"
              text={ViewMoreDetails}
            />
          </div>
        ),
        sortable: true,
        width: "120px",
      },
      {
        name: Action,
        cell: (row: ConveyanceDetail) => (
          <>
            <IconComponent
              className="icofont icofont-ui-delete text-danger f-16 pointer"
              onClick={() => openDialog(row.id)}
            />
          </>
        ),
        sortable: true,
        width: "140px",
      },
    ];
    return baseCols;
  }, [conveyanceList, darkMode]);

  return (
    <div className="table-responsive user-datatable custom-scrollbar mt-4 d-none d-lg-block">
      <CustomDataTable
        striped
        noHeader
        paginationServer
        columns={columns}
        onPageChange={pageChange}
        highlightOnHover
        totalRows={total}
        pageSize={perPage}
        loading={conveyanceListLoading}
        onRowsPerPageChange={rowPerPageChange}
        data={tableData}
        loaderComponent={
          <SkeletonTable
            rows={tableData.length || 8}
            columns={columns}
            rowHeight={40}
            gap={4}
          />
        }
      />
    </div>
  );
};

export default ConveyanceTable;
